"use client";

import {
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import Image from "next/image";
import {
  CountryOption,
  LanguageOption,
  LanguagePickerProps,
} from "components/language-picker/language-picker.props";
import OverlayDialog from "components/overlay-dialog/OverlayDialog";
import Icon from "../../atoms/Icon";
import { ICONS } from "../../variables/icons";
import { extractHiddenBlock } from "../../utils/styles";
import { NoDataFallback } from "../../utils/NoDataFallback";
import { SUPPORTED_LANGUAGES } from "src/i18n/routing";

export const Default = (props: LanguagePickerProps) => {
  console.log("---->[LanguagePicker] Component");

  const { page, fields, params } = props;
  const isPageEditing = page?.mode?.isEditing;
  const locale = page?.locale || process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE;

  const countryBasedOnLocale = fields?.countries?.find((country) =>
    country?.languages?.find((l) => l?.code == locale),
  );
  //For Taiwan, it shares same locale as that of  US. Hence it shows Taiwan as country for en-us
  const defaultCountry =
    locale == "en" || locale == "en-us" || !countryBasedOnLocale
      ? fields?.countries?.find((country) => country.default)
      : countryBasedOnLocale;
  const [country, setCountry] = useState<CountryOption | undefined>(
    defaultCountry,
  );
  const defaultLanguage =
    defaultCountry?.languages?.find((lang) => lang?.code == locale) ||
    defaultCountry?.languages?.find((lang) => lang?.default);

  const [language, setLanguage] = useState<LanguageOption | undefined>(
    defaultLanguage,
  );
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);

  if (!fields) {
    return <NoDataFallback componentName="Language Picker" />;
  }

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const handleLanguageChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selected = country?.languages.find(
      (lang) => lang.code === e.target.value,
    );
    if (selected) {
      setLanguage({ ...selected });
    }
  };

  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selected = fields.countries.find((c) => c.id === e.target.value);
    if (!selected) return;
    setCountry({ ...selected });
    const defaultLanguage = selected.languages.find((lang) => lang.default);
    if (defaultLanguage) {
      setLanguage({ ...defaultLanguage });
    }
  };

  const handleLanguagePickerClick = (
    e: ReactMouseEvent<HTMLSpanElement> | ReactKeyboardEvent<HTMLSpanElement>,
  ) => {
    if (!isPageEditing) {
      e.stopPropagation();
      setShowLanguagePicker(true);
    }
  };

  const handleLanguagePickerKeyDown = (
    e: ReactKeyboardEvent<HTMLSpanElement>,
  ) => {
    if (e.key === "Enter") {
      handleLanguagePickerClick(e);
    }
  };

  const onContinueClick = () => {
    if (defaultLanguage?.code == language?.code) {
      setShowLanguagePicker(false);
      return;
    }
    if (language?.redirect) {
      globalThis.location.href = language.redirect;
      setShowLanguagePicker(false);
    } else {
      // 1. Remove existing locale from path
      const pathname = globalThis.location.pathname || "/";
      const segments = pathname.split("/").filter(Boolean);
      if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
        segments.shift();
      }

      // 2. Build clean path
      const newPath = language?.code
        ? `/${language?.code?.toLowerCase()}/${segments.join("/")}`
        : `/`;

      globalThis.location.href = newPath;
      setShowLanguagePicker(false);
    }
  };

  const handleClose = () => {
    setCountry(defaultCountry);
    setLanguage(defaultLanguage);
    setShowLanguagePicker(false);
  };

  return (
    <div
      className={`languagepicker-component min-h-[40px] ${hiddenBlockStyles}`}
      data-role="languagepicker-component"
    >
      <div>
        <button
          className="lpselect w-auto flex items-center justify-items-center gap-2 xl:px-[16px] xl:pb-[11px] xl:pt-[15px] cursor-pointer xl:hover:bg-[var(--hover-effect-opacity)] font-normal focus:font-medium hover:underline active:underline focus:underline xl:active:no-underline xl:focus:no-underline xl:hover:no-underline"
          onClick={handleLanguagePickerClick}
          onKeyDown={handleLanguagePickerKeyDown}
          data-testid="language-container"
          type="button"
        >
          {country && language && (
            <>
              <Image
                src={`${defaultCountry?.flagImageUrl}`}
                width={18}
                height={12}
                alt={country.name}
                className="w-[18px] h-[12px]"
                data-testid="flagimage"
              />
              <span
                className="leading-[17.6px] text-base font-normal focus:font-medium"
                data-testid="language-code"
              >
                {fields.languageDictionary[
                  defaultLanguage?.code ?? language.code
                ] ||
                  defaultLanguage?.code ||
                  language.code}
              </span>
              <Icon
                icon={ICONS.CHEVRON_WHITE_RIGHT}
                className="block lg:hidden w-4 h-4"
              />
            </>
          )}
        </button>
      </div>
      {showLanguagePicker && (
        <OverlayDialog
          isOpen={showLanguagePicker}
          size="large"
          onClose={handleClose}
          data-testid="languagepicker-dialog"
        >
          {language && (
            <div>
              <div className="lplogo mb-[20px] relative max-h-[100px] min-w-[260px]">
                <Image
                  src="/ecolab-logo_2023.svg"
                  width={260}
                  height={50}
                  alt="Ecolab logo"
                  className="m-auto"
                  data-testid="logo"
                />
              </div>
              <div className="lpcontainer mt-[38px]">
                <div className="mb-[20px]">
                  <h2
                    className="leading-[22px] text-[22px] font-bold text-dark-gray text-center"
                    data-testid="heading"
                  >
                    {fields.titleDictionary[language.code]}
                  </h2>
                </div>
                <div className="text-center">
                  <div className="m-[15px] lg:inline-block">
                    <h3
                      className="leading-[16px] text-base font-normal text-dark-gray text-center mx-0 mt-[8px] mb-[16px] align-baseline"
                      data-testid="country-label"
                    >
                      {fields.countryLabelDictionary[language.code]}
                    </h3>
                    <select
                      name="country"
                      defaultValue={country?.id}
                      onChange={handleCountryChange}
                      className="dropdown-custom w-full lg:w-[280px]"
                      data-testid="select-country"
                      tabIndex={0}
                    >
                      {fields.countries?.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="m-[15px] lg:inline-block">
                    <h3
                      className="leading-[16px] text-base font-normal text-dark-gray text-center mx-0 mt-[8px] mb-[16px] align-baseline"
                      data-testid="language-label"
                    >
                      {fields.languageLabelDictionary[language.code]}
                    </h3>
                    <select
                      name="language"
                      value={language.code}
                      onChange={handleLanguageChange}
                      className="dropdown-custom w-full lg:w-[280px]"
                      data-testid="select-language"
                      tabIndex={0}
                    >
                      {country?.languages?.map((lang, index) => (
                        <option key={`${lang.code}-${index}`} value={lang.code}>
                          {fields.languageDictionary[lang.code] || lang.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="ctawrapper mt-[35px]">
                  <div className="w-[220px] m-auto">
                    <button
                      onClick={onContinueClick}
                      className="bg-ecolab-blue text-white bg-none filter-none border-b-4 border-dark-blue tracking-[.46px] capitalise rounded-md hover:bg-dark-blue focus:bg-dark-blue mb-[5px] p-[12px] w-[220px] cursor-pointer"
                      aria-label={fields.buttonLabelDictionary[language.code]}
                      tabIndex={0}
                      data-site={language.redirect}
                      data-lang={language.code}
                      data-country={language.code}
                      lang={language.code}
                      data-nonmp="false"
                      data-testid="continue"
                    >
                      {fields.buttonLabelDictionary[language.code]}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </OverlayDialog>
      )}
    </div>
  );
};
